import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';

import { JIRA_SUPPORT_LINK } from '../../../../common/constants/index.tsx';
import type { OgIntegration, StoreBaseContainerProps } from '../../../../common/types/index.tsx';
import { createDataResponse, createErrorResponse } from '../../../../common/utils/store/index.tsx';
import { multiplePromiseSettler } from '../../utils/index.tsx';
import * as endpoints from '../endpoints/index.tsx';
import type { ChatToolsState } from '../types.tsx';
import messages from './messages.tsx';

const getChatToolsConnections =
	(): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			connections: {
				data: state.connections.data,
				error: null,
				loading: true,
			},
		});
		return endpoints
			.getConnections(cloudId)
			.then((res) => setState({ connections: createDataResponse(res) }))
			.catch((err) => {
				// TODO we might display toast
				setState({ connections: createErrorResponse(err) });
				throw err;
			});
	};

const authorizeSlackConnection =
	(chatSpaceId: string): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState }, { cloudId }) => {
		setState({ loadingConnectionId: chatSpaceId });
		endpoints
			.getAuthorizeSlackConnectionUrl({ chatSpaceId, cloudId })
			.then((res) => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(res.data.url, '_blank');
			})
			// TODO we might display toast
			.finally(() => {
				setState({ loadingConnectionId: '' });
			});
	};

const delegateMSTConnection =
	(chatSpaceId: string): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState }, { cloudId }) => {
		setState({ loadingConnectionId: chatSpaceId });
		endpoints
			.getDelegateMSTConnectionUrl({ chatSpaceId, cloudId })
			.then((res) => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(res.data.url, '_blank');
			})
			// TODO we might display toast
			.finally(() => {
				setState({ loadingConnectionId: '' });
			});
	};

const getAuthorizeMSTConnectionUrl =
	(chatSpaceId: string): Action<ChatToolsState, StoreBaseContainerProps, Promise<string>> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		if (state.mstConnectionUrls[chatSpaceId]) {
			return Promise.resolve(state.mstConnectionUrls[chatSpaceId]);
		}

		setState({ loadingConnectionId: chatSpaceId });
		return (
			endpoints
				.getaAuthorizeMSTConnectionUrl({ chatSpaceId, cloudId })
				.then((res) => {
					state.mstConnectionUrls[chatSpaceId] = res.data.url;
					return res.data.url;
				})
				// TODO we might display toast
				.finally(() => {
					setState({ loadingConnectionId: '' });
				})
		);
	};

const authorizeMSTConnection =
	(chatSpaceId: string): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ dispatch }) => {
		dispatch(getAuthorizeMSTConnectionUrl(chatSpaceId)).then((url) => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(url, '_blank');
		});
	};

const copyMSTAuthorizationLink =
	(chatSpaceId: string): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ dispatch }, { showFlag }) => {
		dispatch(getAuthorizeMSTConnectionUrl(chatSpaceId)).then((url) => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			(fg('jfp-magma-undefined-navigator') ? globalThis.navigator.clipboard : navigator.clipboard)
				.writeText(url || '')
				.then(() => {
					showFlag?.({
						messageId:
							'eoc-changeboarding.services.store.chat-tools.actions.show-flag.success.copied-success',
						messageType: 'transactional',
						type: 'success',
						title: messages.copiedConfirmationTitleNonFinal,
						description: messages.copiedSuccessNonFinal,
					});
				})
				.catch(() => {
					showFlag?.({
						messageId:
							'eoc-changeboarding.services.store.chat-tools.actions.show-flag.error.copied-failed',
						messageType: 'transactional',
						type: 'error',
						title: messages.copiedConfirmationTitleNonFinal,
						description: messages.copiedFailedNonFinal,
					});
				});
		});
	};

const getIntegrations =
	(): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			integrations: {
				data: state.integrations.data,
				error: null,
				loading: true,
			},
		});
		return endpoints
			.getOgIntegrations(cloudId)
			.then((res) => setState({ integrations: createDataResponse(res) }))
			.catch((err) => {
				// TODO we might display toast
				setState({ integrations: createErrorResponse(err) });
				throw err;
			});
	};

const givePermissionToUser =
	(integration: OgIntegration): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState }, { cloudId }) => {
		setState({ loadingIntegrationId: integration.id });
		endpoints
			.getUserAuthorizationUrl({
				cloudId,
				chatSpaceType: integration.chatSpaceType,
				chatSpaceId: integration.chatSpaceId,
			})
			.then((res) => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(res.data.url, '_blank');
			})
			// TODO we might display toast
			.finally(() => {
				setState({ loadingIntegrationId: '' });
			});
	};

const duplicateIntegration =
	(integration: OgIntegration): Action<ChatToolsState, StoreBaseContainerProps> =>
	({ setState, dispatch }, { cloudId, showFlag }) => {
		setState({ loadingIntegrationId: integration.id });
		endpoints
			.duplicateIntegration({
				cloudId,
				id: integration.id,
				channelId: integration.channelId,
				connectionId: integration.connectionId,
				msTeamsId: integration.msTeamsId,
			})
			.then(() => {
				dispatch(getIntegrations());
			})
			.catch((error) => {
				if (error.statusCode === 403) {
					showFlag?.({
						messageId:
							'eoc-changeboarding.services.store.chat-tools.actions.show-flag.error.duplicate-not-part-of-channel',
						messageType: 'transactional',
						type: 'error',
						title: messages.duplicateNotPartOfChannelTitleNonFinal,
						description: messages.duplicateNotPartOfChannelDescriptionNonFinal,
					});
					return;
				}
				showFlag?.({
					messageId:
						'eoc-changeboarding.services.store.chat-tools.actions.show-flag.error.duplicate-generic-error',
					messageType: 'transactional',
					type: 'error',
					title: messages.duplicateGenericErrorTitleNonFinal,
					description: messages.duplicateGenericErrorDescriptionNonFinal,
					actions: [
						{
							content: messages.contactSupportBtnLabelNonFinal,
							href: JIRA_SUPPORT_LINK,
							target: '_blank',
						},
					],
				});
			})
			// TODO we might display toast
			.finally(() => {
				setState({ loadingIntegrationId: '' });
			});
	};

const initialize =
	(): Action<ChatToolsState, StoreBaseContainerProps, Promise<PromiseSettledResult<void>[]>> =>
	({ dispatch }) =>
		Promise.allSettled([dispatch(getChatToolsConnections()), dispatch(getIntegrations())]).then(
			multiplePromiseSettler,
		);

const initializeForTeamAdmin =
	(): Action<ChatToolsState, StoreBaseContainerProps, Promise<PromiseSettledResult<void>[]>> =>
	({ dispatch }) =>
		Promise.allSettled([dispatch(getIntegrations())]).then(multiplePromiseSettler);

export const actions = {
	getChatToolsConnections,
	authorizeSlackConnection,
	delegateMSTConnection,
	authorizeMSTConnection,
	copyMSTAuthorizationLink,
	getIntegrations,
	givePermissionToUser,
	duplicateIntegration,
	initialize,
	initializeForTeamAdmin,
};
